
import {defer as observableDefer, of as observableOf,  Observable } from 'rxjs';
import {take, catchError, map, switchMap, delay, exhaustMap, withLatestFrom, tap} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router  } from '@angular/router';
import { Authenticate } from '../models/user';
import * as Action from '../store/actions';
import { LoginService } from '../login.service';
import { State } from '../../app.reducer';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {LoggingService} from "../../shared/logging.service";

@Injectable()
export class Effects {

  public isDemo: boolean = environment.demo;

  /* Trying to login the user */

  @Effect()
  login: Observable<Action.LoginFailure | Action.LoginSuccess | Action.RequireMFA | Action.ForceMFA> = this.actions$.pipe(
      ofType(Action.LOGIN),
      delay(new Date(Date.now() + 100)),
      exhaustMap((action: Action.Login) => {
        let request : Observable<any>;

        if ((action.payload as Authenticate).username) {
          request = this.loginService.login(action.payload);
        }

        //if (!this.isDemo &&
        //    action.payload.username === 'demo') {
        //  return observableOf(new Action.LoginFailure("demo_account"))
        //}

        return request.pipe(switchMap(payload => { // Retrieve user from Kerberos API.
            return (payload.data && payload.data.token) ? this.loginService.withAPI(payload.data.token) : observableOf(payload.data);
          }),
          map(profile => {
            if(profile && !profile.forcemfa && !profile.twofactor) {
              this.log.Identify(action.payload.username, profile.email, action.payload.provider)
              // store user details and jwt token in local storage to keep user logged in between page refreshes
              localStorage.setItem('currentUser', JSON.stringify(profile));
              return new Action.LoginSuccess(profile);
            }
            if(profile && profile.twofactor) {
              return new Action.RequireMFA(profile);
            }
            if(profile && profile.forcemfa) {
              return new Action.ForceMFA(profile);
            }
          }),
          catchError(error => {
            //mixpanel.track("loginUserFailed", {username: action.payload.username, error: error});
            return observableOf(new Action.LoginFailure(error.error.error_code))
          }),)
      }),);


  /* Validate MFA Token*/
  @Effect()
  validateMFAToken: Observable<Action.ValidateMFASuccess | Action.ValidateMFAFailure> = this.actions$.pipe(
    ofType(Action.VALIDATE_MFA),
    switchMap((action: Action.ValidateMFA) => {
      return this.loginService.validateMFA(action.payload).pipe(map((data) => {
        return new Action.ValidateMFASuccess({success: true});
      }),
      catchError((e: HttpErrorResponse) => {
        //mixpanel.track("validateMFATokenFailed", {token: action.payload, error: e});
        return observableOf(new Action.ValidateMFAFailure(e.error.message));
      }),);
    }),);


    /* Get the SSO domains */

  @Effect()
  ssoDomains: Observable<Action.GetSSODomainsFailure | Action.GetSSODomainsSuccess> = this.actions$.pipe(
    ofType(Action.GET_SSO_DOMAINS),
    switchMap((action: Action.GetSSODomains) => {
      return this.loginService.getSSODomains().pipe(take(1), map((payload) => {
        return new Action.GetSSODomainsSuccess(payload);
      }),
      catchError((e: HttpErrorResponse) => {
        return observableOf(new Action.GetSSODomainsFailure(e.error.message));
      }),);
    }),);

  /* Received a JWT token (generated from the SSO token) */
  @Effect()
  sso: Observable<Action.SingleSignOnFailure | Action.SingleSignOnSuccess> = this.actions$.pipe(
      ofType(Action.SSO),
      switchMap((action: Action.SingleSignOn) => {
        const token = action.payload;
        return this.loginService.withAPI(token).pipe(map((profile) => {
            if(profile) {

              // Send to logging service.
              this.log.Identify(action.payload.username, profile.email, action.payload.provider)

              // store user details and jwt token in local storage to keep user logged in between page refreshes
              localStorage.setItem('currentUser', JSON.stringify(profile));
              return new Action.SingleSignOnSuccess(profile);
            }
          }),
          catchError((error: HttpErrorResponse) => {
            return observableOf(new Action.SingleSignOnFailure(error.error.error))
          }),);
      }),);

  @Effect({ dispatch: false })
  ssoSuccess = this.actions$.pipe(
      ofType(Action.SSO_SUCCESS),
      withLatestFrom(this.store$),
      tap(([action, state]) => { // Get route from store.
        const path = window.location.href;
        var parser = document.createElement('a');
        parser.href = path;
        window.location.href = parser.protocol + "//" + parser.host
      }),);

  /* Trying to register the user */

  @Effect()
  register: Observable<Action.RegisterFailure | Action.RegisterSuccess> = this.actions$.pipe(
    ofType(Action.REGISTER),
    switchMap((action: Action.Register) => {
      return this.loginService.register(action.payload).pipe(map((subscription) => {
        //mixpanel.track("registerUser", {username: data.username, email: data.email});
        return new Action.RegisterSuccess({success: true});
      }),
      catchError((e: HttpErrorResponse) => {
        const data = action.payload;
        //mixpanel.track("registerUserFailed", {username: data.username, email: data.email, error: e});
        return observableOf(new Action.RegisterFailure(e.error.message));
      }),);
    }));

  /* Redirect to login after registration */
  @Effect({ dispatch: false })
  registerSuccess = this.actions$.pipe(
    ofType(Action.REGISTER_SUCCESS),
    withLatestFrom(this.store$),
    tap(([action, state]) => {
      this.router.navigate(['/login']);
    }),);

  /* Send a forgot password */

  @Effect()
  forgot: Observable<Action.ForgotFailure | Action.ForgotSuccess> = this.actions$.pipe(
    ofType(Action.FORGOT),
    switchMap((action: Action.Forgot) => {
      const data = action.payload;
      return this.loginService.forgot({
        usernameOrEmail: data.usernameOrEmail,
        domain: data.domain,
      }).pipe(map((forgot) => {
        //mixpanel.track("forgotPassword", {usernameOrEmail: data.usernameOrEmail});
        return new Action.ForgotSuccess({success: true});
      }),
      catchError((e: HttpErrorResponse) => {
        const data = action.payload;
        //mixpanel.track("forgotPasswordFailed", {usernameOrEmail: data.usernameOrEmail, error: e});
        return observableOf(new Action.ForgotFailure(e.error.message));
      }),);
    }));

  /* Trying to activating the user */

  @Effect()
  activate: Observable<Action.ActivateFailure | Action.ActivateSuccess> = this.actions$.pipe(
    ofType(Action.ACTIVATE),
    delay(new Date(Date.now() + 1000)),
    switchMap((action: Action.Register) => {
      return this.loginService.activate(action.payload).pipe(map((data) => {
        return new Action.ActivateSuccess({success: true});
      }),
      catchError((e: HttpErrorResponse) => {
        return observableOf(new Action.ActivateFailure(e.error.message));
      }),);
    }),);

  /* Check if user is logged in */

  @Effect()
  checkIfLoggedIn: Observable<Action.LoginFailure | Action.LoginSuccess> = this.actions$.pipe(
    ofType(Action.LOGIN_CHECK),
    map((action: Action.CheckIfLoggedIn) => action.payload),
    switchMap(payload => {
      let currentUser = JSON.parse(localStorage.getItem('currentUser'));
      return (currentUser && currentUser.accesstoken) ? this.loginService.withAPI(currentUser.accesstoken) : observableOf(false);
    }),
    take(1),
    map(profile => {
        if (profile) {
          /// User logged in
          return new Action.LoginSuccess(profile);
        } else {
          /// User not logged in, do nothing..
          return new Action.LoginFailure(null);
        }
    }),
    catchError(error =>  observableOf(new Action.LoginFailure(error.error.error)) ),);


  @Effect({ dispatch: false })
  loginSuccess = this.actions$.pipe(
    ofType(Action.LOGIN_SUCCESS),
    withLatestFrom(this.store$),
    tap(([action, state]) => { // Get route from store.
      const currentUrl = window.location.href;
      const findSuffix = currentUrl.search(/login/i);
      const numberOfElements = currentUrl.split("/").length;
      if(findSuffix > -1){
        this.router.navigate(['/']);
      } else if(numberOfElements === 4 && currentUrl.substr(currentUrl.length-1) === "/") {
        this.router.navigate(['/']);
      }
    }),);

  @Effect({ dispatch: false })
  loginFailed = this.actions$.pipe(
    ofType(Action.LOGIN_FAILURE),
    withLatestFrom(this.store$),
    tap(([action, state]) => { // Get route from store.
      this.router.navigate(['/login']);
    }),);

  /* Requesting to logout the current user */

  @Effect()
  logout: Observable<Action.LogoutSuccess> = this.actions$.pipe(
      ofType(Action.LOGOUT),
      map(() => {
        this.loginService.logout();
        return new Action.LogoutSuccess()
      }));

  @Effect({ dispatch: false })
  logoutSuccess = this.actions$.pipe(
    ofType(Action.LOGOUT_SUCCESS),
    tap(() => {
      //mixpanel.track("logoutUser");
      localStorage.removeItem('currentUser');
      this.router.navigate(['/login']);
    }));

  /*  Auto start CheckIfLoggedIn action on init */

  @Effect()
  init$: Observable<Action.CheckIfLoggedIn> = observableDefer(() => {
    // this is a hack for knowing the url before the store is loaded.
    // the idea is that for specific urls there is no checkloggedin required.
    const currentUrl = window.location.href;
    if( !currentUrl.includes('/login/activate') &&
        !currentUrl.includes('/login/sso') &&
        !currentUrl.includes('/signup') &&
        !currentUrl.includes('/wall')) {
      return observableOf(new Action.CheckIfLoggedIn());
    }
  });

  constructor(private loginService: LoginService,
              private store$: Store<State>,
              private actions$: Actions,
              private router: Router,
              private log: LoggingService) {}
}
